import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { subscriptionService } from '../lib/subscriptionService';
import toast from 'react-hot-toast';

export type SubscriptionStatus = 'active' | 'inactive' | 'bypass' | null;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  subscription: any | null; // Adding subscription object to context
  subscriptionStatus: SubscriptionStatus;
  subscriptionLoading: boolean;
  isPaywallEnabled: boolean;
  isCheckingPayment: boolean;
  checkSubscriptionStatus: (forceCheck?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  subscription: null,
  subscriptionStatus: null,
  subscriptionLoading: true,
  isPaywallEnabled: false,
  isCheckingPayment: false,
  checkSubscriptionStatus: async () => { }
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isPaywallEnabled, setIsPaywallEnabled] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);

  // Function to check subscription status
  const checkSubscriptionStatus = async (forceCheck = false) => {
    // When forceCheck is true, we skip cache and check directly with the database
    // We'll use this flag when polling for recent subscription changes
    if (!user) {
      setSubscriptionStatus(null);
      setSubscription(null);
      setSubscriptionLoading(false);
      return;
    }

    console.log('Checking subscription status for user:', user.id, user.email);
    setSubscriptionLoading(true);

    // Create a timeout promise to avoid hanging
    const timeoutPromise = new Promise<'inactive'>((resolve) => {
      setTimeout(() => {
        console.warn('Subscription status check timed out, defaulting to inactive');
        resolve('inactive');
      }, 8000); // 8 second timeout - increased to allow for network latency
    });

    try {
      // Create a controller to allow aborting the fetch if it times out
      const controller = new AbortController();

      // Check if there's a direct record in the database first (most reliable check)
      try {
        // If forceCheck is true, we want to make sure we're getting fresh data, not cached
        console.log('Performing direct database check for subscription', forceCheck ? '(forced refresh)' : '');
        const { data: subData, error: subError } = await supabase
          .from('user_subscriptions')
          .select('*') // Select all fields including plan_type
          .eq('user_id', user.id)
          .maybeSingle();

        if (subError) {
          console.error('Error checking subscription in database:', subError);
        }

        if (subData?.subscription_status === 'active') {
          console.log('Found active subscription in database');
          setSubscriptionStatus('active');
          setSubscription(subData); // Store full subscription data
          setSubscriptionLoading(false);
          return;
        }
        // Even if not active, store the data so we can check plan_type (e.g. for free tier)
        if (subData) {
          setSubscription(subData);
          if (subData.plan_type === 'free') {
            setSubscriptionStatus('active');
            setSubscriptionLoading(false);
            return;
          }
        }
      } catch (dbErr) {
        console.error('Exception during database subscription check:', dbErr);
      }

      // If timeout occurs, abort the fetch
      const timeoutId = setTimeout(() => controller.abort(), 7500);

      // First get paywall settings
      let settings;
      try {
        settings = await Promise.race([
          subscriptionService.getPaywallSettings(true), // Force refresh
          timeoutPromise.then(() => {
            console.warn('Paywall settings check timed out, using fallback');
            return { paywall_enabled: false, grace_period_days: 3, test_mode: true };
          })
        ]);
        clearTimeout(timeoutId);
      } catch (e) {
        console.warn('Error fetching paywall settings:', e);
        settings = { paywall_enabled: false, grace_period_days: 3, test_mode: true };
      }

      setIsPaywallEnabled(settings.paywall_enabled);

      // If paywall is not enabled, we can skip subscription check
      if (!settings.paywall_enabled) {
        setSubscriptionStatus('bypass');
        setSubscriptionLoading(false);
        return;
      }

      // Then check user's subscription status with timeout protection and graceful fallback
      try {
        // First check URL for subscription=success parameter
        // This ensures users coming back from Stripe get immediate access
        const currentUrl = window.location.href;
        const hasSuccessParam = currentUrl.includes('subscription=success');

        if (hasSuccessParam) {
          console.log('Subscription success parameter detected in URL - granting access');
          setSubscriptionStatus('active');
          setSubscriptionLoading(false);
          // Store this in localStorage for persistence across page reloads
          localStorage.setItem('subscription_success_detected', 'true');
          localStorage.setItem('subscription_success_timestamp', new Date().getTime().toString());
          return;
        }

        // Check if we have a recent subscription success in localStorage (multiple possible keys)
        // We check multiple keys because different parts of our flow might set different ones
        const recentDetected = localStorage.getItem('subscription_success_detected');
        const recentSuccess = localStorage.getItem('subscription_success');
        const successTimestamp = localStorage.getItem('subscription_success_timestamp') ||
          localStorage.getItem('subscription_success_time');

        // Log what we found for debugging
        console.log('Checking localStorage for payment success indicators:', {
          recentDetected,
          recentSuccess,
          successTimestamp
        });

        // If any of the success indicators are set
        if ((recentDetected === 'true' || recentSuccess === 'true') && successTimestamp) {
          const timestamp = parseInt(successTimestamp);
          const now = new Date().getTime();
          // If we detected success in the last 24 hours, grant access
          if (now - timestamp < 24 * 60 * 60 * 1000) {
            console.log('Recent subscription success detected in localStorage - granting access');
            setSubscriptionStatus('active');
            setSubscriptionLoading(false);

            // Also save this success state to the other localStorage keys for consistency
            localStorage.setItem('subscription_success_detected', 'true');
            localStorage.setItem('subscription_success', 'true');
            localStorage.setItem('subscription_success_timestamp', now.toString());
            localStorage.setItem('subscription_success_time', now.toString());

            return;
          }
        }

        // Perform a direct database check first - more reliable than the service call

        try {
          const { data: subData, error: subError } = await supabase
            .from('user_subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();

          console.log('Direct subscription check:', {
            user_id: user.id,
            found: !!subData,
            subscription_data: subData,
            error: subError
          });

          // If we find a subscription record with active status, grant access immediately
          if (subData) {
            setSubscription(subData); // Update subscription state
            if (subData.subscription_status === 'active' ||
              subData.bypass_subscription === true ||
              subData.plan_type === 'free') {
              console.log('Found active/free/bypass subscription in database - granting access');
              setSubscriptionStatus('active');
              setSubscriptionLoading(false);
              return;
            }
          }
        } catch (dbErr) {
          console.warn('Error during direct subscription check:', dbErr);
        }

        // If direct check didn't find anything, try the service call with timeout
        const status = await Promise.race([
          subscriptionService.getUserSubscriptionStatus(user.id),
          timeoutPromise
        ]);

        console.log('Subscription status result:', status);
        setSubscriptionStatus(status);
      } catch (e) {
        console.warn('Subscription status check failed, defaulting to active to avoid disruption:', e);
        // Default to active to prevent locking users out due to temporary API issues
        setSubscriptionStatus('active');
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Default to active to prevent locking users out due to errors
      setSubscriptionStatus('active');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('Error getting auth session:', error);
        toast.error('Authentication error. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_OUT') {
        toast.success('Successfully signed out');
        setSubscriptionStatus(null);
        setSubscription(null);
      } else if (event === 'SIGNED_IN') {
        toast.success('Successfully signed in');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Check subscription status whenever user changes
  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    }
  }, [user]);

  // Add polling effect for users who might have just paid
  useEffect(() => {
    if (!user || subscriptionStatus === 'active') return;

    // Check if we're returning from payment based on URL or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const isPaymentReturn =
      urlParams.get('payment_complete') === 'true' ||
      urlParams.get('restore_session') === 'true' ||
      localStorage.getItem('subscription_success') === 'true' ||
      localStorage.getItem('subscription_success_detected') === 'true';

    console.log('Checking if returning from payment:', { isPaymentReturn });

    // Check if we haven't already started polling
    if (!isCheckingPayment && isPaymentReturn) {
      console.log('Starting subscription status polling - User is returning from payment');
      setIsCheckingPayment(true);

      let pollCount = 0;
      const maxPolls = 12; // Will poll for 60 seconds total (12 x 5s)

      const pollInterval = setInterval(async () => {
        pollCount++;
        console.log(`Polling for subscription status (${pollCount}/${maxPolls})`);

        // Force a direct DB check 
        await checkSubscriptionStatus(true);

        // If we found an active subscription or reached max polls, stop polling
        // Using String() to avoid TypeScript strict comparison issues
        const isActive = String(subscriptionStatus) === 'active';
        if (isActive || pollCount >= maxPolls) {
          console.log('Ending subscription polling:',
            isActive ? 'Active subscription found' : 'Max polls reached');
          setIsCheckingPayment(false);
          clearInterval(pollInterval);
        }
      }, 5000); // Check every 5 seconds

      // Safety cleanup
      return () => {
        clearInterval(pollInterval);
        setIsCheckingPayment(false);
      };
    }
  }, [user, subscriptionStatus]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      subscription,
      subscriptionStatus,
      subscriptionLoading,
      isPaywallEnabled,
      isCheckingPayment,
      checkSubscriptionStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
}